import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SnackService } from './snack.service';
import { Observable } from 'rxjs';
import { serverurl } from '../../environments/environment';
import { GenericChatTypesUi } from '../interfaces/responsedata.ts/generic-streams';
import { ClipboardService } from './clipboard.service';

@Injectable({
    providedIn: 'root'
})
export class GenAiService {
    constructor(
        private http: HttpClient,
        private snack: SnackService,
        private clipboardService: ClipboardService,
    ) { }
    async cleanAiResponse(promptId: string, content: any): Promise<boolean> {
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/clean-airesponse', { promptId, content }).subscribe({
                next: (data: any) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async genAiMiroExportResponseText(content: any): Promise<false | string> {
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/clean-airesponse', { "content": JSON.stringify(content), "promptId": "GEN_EXPORT_MIRO" }).subscribe({
                next: async (data: any) => {
                    let dataParsed = data.result.substring(data.result.indexOf('|'));
                    resolve(dataParsed);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async genAiMiroExport(content: any): Promise<boolean> {
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/clean-airesponse', { "content": JSON.stringify(content), "promptId": "GEN_EXPORT_MIRO" }).subscribe({
                next: async (data: any) => {
                    let dataParsed = data.result.substring(data.result.indexOf('|'));

                    await this.clipboardService.copyToClipboard(dataParsed);
                    resolve(true);
                },
                error: (err) => {
                    // this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    genericAiPost(streamId: string, stepId: string, promptType: GenericChatTypesUi, content: string, stepNumber:number, promptId: string | undefined = undefined, threadId: string | undefined = undefined): Promise<{ data: any, type: string } | false> {
        return new Promise((resolve) => {
            this.http.post(`${serverurl}/aiservice/stream`, { streamId, stepId, promptType, content, promptId, threadId, stepNumber }).subscribe({
                next: (data: any) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    genericStreamClosingAiPost(streamId: string, promptId: string): Promise<{ result: any } | false> {
        return new Promise((resolve) => {
            this.http.post(`${serverurl}/aiservice/stream-closing`, { streamId, promptId }).subscribe({
                next: (data: any) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    genericStreamAiPost(streamId: string, stepId: string, promptType: GenericChatTypesUi, content: string, stepNumber:number, promptId: string | undefined = undefined, threadId: string | undefined = undefined): Observable<{
        type: 'meta' | 'token' | 'done' | 'error';
        text: string;
        token?: string;
        meta?: any;
        error?: string;
    }> {
        return new Observable(observer => {
            const controller = new AbortController();

            (async () => {
                try {
                    const resp = await fetch(`${serverurl}/aiservice/stream`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'text/event-stream',
                        },
                        credentials: 'include', // wichtig wegen Session-Cookie
                        body: JSON.stringify({ streamId, stepId, promptType, content, promptId, threadId, stepNumber }),
                        signal: controller.signal,
                    });

                    if (!resp.ok || !resp.body) {
                        // Parse error response body to get msg object
                        let errorData: any;
                        try {
                            errorData = await resp.json();
                            this.snack.error(errorData);
                        } catch (parseError) {
                            const errorMsg = `error.systemError`;
                            observer.error(new Error(errorMsg));
                        }
                        observer.next({ type: 'error', error: errorData.msg.text, text: errorData.msg.text });
                        observer.complete();
                        return;
                    }

                    const reader = resp.body.getReader();
                    const decoder = new TextDecoder('utf-8');
                    let buffer = '';
                    let aggregated = '';

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        buffer += decoder.decode(value, { stream: true });

                        // SSE-Frames sind durch \n\n getrennt
                        const frames = buffer.split('\n\n');
                        buffer = frames.pop() || '';

                        for (const frame of frames) {
                            const lines = frame.split('\n').filter(Boolean);
                            let eventName = 'message';
                            const dataLines: string[] = [];

                            for (const line of lines) {
                                if (line.startsWith('event:')) {
                                    eventName = line.slice(6).trim();
                                } else if (line.startsWith('data:')) {
                                    dataLines.push(line.slice(5).trim());
                                } else if (line.startsWith(':')) {
                                    // Kommentar/Heartbeat -> ignorieren
                                }
                            }

                            const dataRaw = dataLines.join('\n');

                            if (eventName === 'meta') {
                                let meta: any = dataRaw;
                                try { meta = JSON.parse(dataRaw); } catch { }
                                observer.next({ type: 'meta', meta, text: aggregated });
                                continue;
                            }

                            if (eventName === 'done') {
                                observer.next({ type: 'done', text: aggregated });
                                observer.complete();
                                return;
                            }

                            if (eventName === 'error') {
                                let message = dataRaw;
                                try { message = JSON.parse(dataRaw)?.message ?? dataRaw; } catch { }
                                observer.next({ type: 'error', error: message, text: aggregated });
                                observer.complete();
                                return;
                            }

                            // Standard-"message" (Tokens)
                            if (dataRaw) {
                                try {
                                    const payload = JSON.parse(dataRaw);
                                    if (payload?.token) {
                                        aggregated += payload.token;
                                        observer.next({ type: 'token', token: payload.token, text: aggregated });
                                    }
                                } catch {
                                    // Ungültiges JSON im Datenfeld ignorieren
                                }
                            }
                        }
                    }

                    // Falls Server ohne 'done' beendet hat:
                    observer.next({ type: 'done', text: aggregated });
                    observer.complete();

                } catch (err: any) {
                    // Bei manuellem Abbruch kein Fehler pushen
                    if (!controller.signal.aborted) {
                        observer.error(err);
                    }
                }
            })();

            // Teardown: Stream sauber abbrechen
            return () => controller.abort();
        });
    }

}
